import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { aiApi } from '../services/aiApi';
import { useAIJobStatus } from './useAIJobStatus';
import { boatKeys } from './useBoats';
import { suggestedUpdatesKeys } from './useSuggestedUpdates';

export function useAISpecLookup(boatId: string) {
  const [jobId, setJobId] = useState<string | undefined>();
  const queryClient = useQueryClient();

  const trigger = useMutation({
    mutationFn: () => aiApi.lookupSpecs(boatId),
    onSuccess: (data) => {
      setJobId(data.job_id);
    },
  });

  const jobStatus = useAIJobStatus(jobId);

  // #1307 — the result lands server-side as reviewed suggestions; once the job
  // completes the banner and the record re-read. Once per job: the status query
  // can refetch, and the Centrifugo `done` patch arrives before the result.
  const announcedJobRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!jobId || jobStatus.data?.status !== 'completed' || announcedJobRef.current === jobId) return;
    announcedJobRef.current = jobId;
    void queryClient.invalidateQueries({ queryKey: suggestedUpdatesKeys.item('boats', boatId) });
    void queryClient.invalidateQueries({ queryKey: boatKeys.detail(boatId) });
    // Neutral: the lookup may have found nothing new; the banner says what it did.
    toast('Spec lookup finished — new values appear under Suggested updates.');
  }, [jobId, jobStatus.data?.status, boatId, queryClient]);

  const reset = () => {
    setJobId(undefined);
  };

  return {
    trigger,
    jobStatus,
    jobId,
    reset,
    isActive: !!jobId && jobStatus.data?.status !== 'completed' && jobStatus.data?.status !== 'failed',
  };
}
